import React, { useState } from "react";
import Seo from "../components/Seo";
import { Check, ArrowRight } from "lucide-react";
import { SERVICES } from "../mock";
import { useToast } from "../hooks/use-toast";
import { submitEnquiry } from "../api";

const Services = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: "Please complete the form", description: "Name and email are required." });
      return;
    }
    setLoading(true);
    try {
      await submitEnquiry({ ...form, source: "services" });
      toast({ title: "Enquiry sent", description: "Thank you — our team will be in touch very soon." });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast({ title: "Something went wrong", description: "Please try again in a moment." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Seo title="Estate Agency Services — Selling, Lettings & Valuations" description="Anadear Estates offers expert selling, lettings & management, free property valuations and investment advice across Bath & Bristol." path="/services" />
      <section className="relative h-[340px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1550647512-8b8a24d4f646" alt="Our Services" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative z-10 text-center px-5">
          <p className="text-[12px] tracking-[0.28em] uppercase text-[#e8d4b8] mb-3">What We Do</p>
          <h1 className="font-display text-white text-[40px] md:text-[56px] font-300">Our Services</h1>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 space-y-20">
          {SERVICES.map((s, i) => (
            <div key={s.id} id={s.id} className="grid lg:grid-cols-2 gap-12 items-center scroll-mt-28">
              <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                <p className="text-[12px] tracking-[0.28em] uppercase text-[#b8935f] mb-3">{`0${i + 1}`}</p>
                <h2 className="font-display text-[30px] md:text-[38px] font-300 text-[#2b2b2b] mb-3">{s.title}</h2>
                <p className="font-display text-[18px] text-[#b8935f] mb-5">{s.tagline}</p>
                <p className="text-[15px] text-[#6a6a6a] leading-relaxed mb-7">{s.description}</p>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {s.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-2.5">
                      <Check size={17} className="text-[#b8935f] mt-0.5 flex-shrink-0" />
                      <span className="text-[14px] text-[#4a4a4a]">{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                <img
                  src={[
                    "https://images.unsplash.com/photo-1598928506311-c55ded91a20c",
                    "https://images.unsplash.com/photo-1556911220-bff31c812dba",
                    "https://images.unsplash.com/photo-1740596400206-f894025300b5",
                    "https://images.pexels.com/photos/20418771/pexels-photo-20418771.jpeg",
                  ][i]}
                  alt={s.title}
                  className="rounded-2xl w-full h-[400px] object-cover shadow-[0_16px_50px_rgba(0,0,0,0.1)]"
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="valuation" className="py-24 bg-[#faf7f2] scroll-mt-24">
        <div className="max-w-[680px] mx-auto px-5">
          <div className="text-center mb-10">
            <p className="text-[12px] tracking-[0.28em] uppercase text-[#b8935f] mb-3">Get In Touch</p>
            <h2 className="font-display text-[30px] md:text-[40px] font-300 text-[#2b2b2b] mb-3">Book a free Valuation</h2>
            <p className="text-[15px] text-[#6a6a6a]">Tell us a little about your property and we'll be in touch to arrange your free, no-obligation valuation.</p>
          </div>
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 border border-[#f0ece5] shadow-[0_12px_40px_rgba(0,0,0,0.05)] space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="h-[52px] px-4 rounded-lg border border-[#e2d8c8] text-[15px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] transition" />
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone number" className="h-[52px] px-4 rounded-lg border border-[#e2d8c8] text-[15px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] transition" />
            </div>
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email address" className="w-full h-[52px] px-4 rounded-lg border border-[#e2d8c8] text-[15px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] transition" />
            <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your property..." rows={4} className="w-full px-4 py-3 rounded-lg border border-[#e2d8c8] text-[15px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] transition resize-none" />
            <button type="submit" disabled={loading} className="w-full h-[54px] rounded-lg bg-[#b8935f] text-white text-[15px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {loading ? "Sending..." : <>Request Valuation <ArrowRight size={17} /></>}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Services;
